import { useEffect, useRef, useState, useMemo } from "react"
import {
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
  InputAdornment,
  CircularProgress, // Added this
} from "@mui/material"
import { Html5QrcodeScanner } from "html5-qrcode"
import useScanDetection from "use-scan-detection"
import SearchIcon from "@mui/icons-material/Search"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import DataTable from "examples/Tables/DataTable"
import MDSnackbar from "components/MDSnackbar"

// Service
import * as packageService from "services/packages/package.service"

// Data
import trackingPackagesDataTable from "./data/trackingPackagesDataTable"

function Packages() {
  const rowsPerPage = 10
  const [companyName, setCompanyName] = useState("all")

  // --- Master Data Objects (All data from API) ---
  const [returnedObject, setReturnedObject] = useState(null)
  const [missingObject, setMissingObject] = useState(null)

  const [isReturnedDataLoading, setIsReturnedDataLoading] = useState(true)
  const [isMissingDataLoading, setIsMissingDataLoading] = useState(true)

  // --- Search & Pagination states ---
  const [searchReturned, setSearchReturned] = useState("")
  const [currentReturnedPage, setCurrentReturnedPage] = useState(1)

  const [searchMissing, setSearchMissing] = useState("")
  const [currentMissingPage, setCurrentMissingPage] = useState(1)

  const [barcode, setBarcode] = useState("Pas de scan de code-barres")
  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    content: "",
    color: "primary",
    icon: "",
  })

  const scannerRef = useRef(null)
  useScanDetection({ onComplete: setBarcode, minLength: 3 })

  // --- 1. INITIAL DATA LOAD ---
  const loadInitialData = async () => {
    try {
      setIsReturnedDataLoading(true)
      setIsMissingDataLoading(true)

      const [resReturned, resMissing] = await Promise.all([
        packageService.trackingReturnedPackages(20000, 1),
        packageService.trackingMissingPackages(20000, 1),
      ])

      setReturnedObject(resReturned.data)
      setMissingObject(resMissing.data)
    } catch (error) {
      console.error("Initial load error:", error)
      setReturnedObject(null)
      setMissingObject(null)
    } finally {
      setIsReturnedDataLoading(false)
      setIsMissingDataLoading(false)
    }
  }

  useEffect(() => {
    loadInitialData()
  }, [])

  // --- 2. HELPER: FILTER LOGIC ---
  const getFilteredList = (dataObject, selectedCompany, searchText) => {
    if (!dataObject) return []
    let list = []
    if (selectedCompany === "all") {
      list = [
        ...(dataObject.alamana?.packageList || []),
        ...(dataObject.lazajella?.packageList || []),
        ...(dataObject.mylerz?.packageList || []),
        ...(dataObject.chahin?.packageList || []),
      ]
    } else {
      list = dataObject[selectedCompany]?.packageList || []
    }

    if (!searchText) return list
    const lowerSearch = searchText.toLowerCase()
    return list.filter(
      (pkg) => pkg.barcode?.toLowerCase().includes(lowerSearch) || pkg.clientPhoneNumber?.includes(searchText)
    )
  }

  // --- 3. COMPUTED DATA ---
  const allFilteredReturned = useMemo(
    () => getFilteredList(returnedObject, companyName, searchReturned),
    [returnedObject, companyName, searchReturned]
  )

  const paginatedReturned = useMemo(() => {
    const start = (currentReturnedPage - 1) * rowsPerPage
    return allFilteredReturned.slice(start, start + rowsPerPage)
  }, [allFilteredReturned, currentReturnedPage])

  const allFilteredMissing = useMemo(
    () => getFilteredList(missingObject, companyName, searchMissing),
    [missingObject, companyName, searchMissing]
  )

  const paginatedMissing = useMemo(() => {
    const startIdx = (currentMissingPage - 1) * rowsPerPage
    return allFilteredMissing.slice(startIdx, startIdx + rowsPerPage)
  }, [allFilteredMissing, currentMissingPage])

  // --- 4. HANDLERS ---
  const handleReturnedSearch = (e) => {
    setSearchReturned(e.target.value)
    setCurrentReturnedPage(1)
  }

  const handleMissingSearch = (e) => {
    setSearchMissing(e.target.value)
    setCurrentMissingPage(1)
  }

  const handleCloseSnackbar = () => setSnackbarObject((p) => ({ ...p, open: false }))
  const pageCount = (total) => Math.ceil(total / rowsPerPage) || 1

  return (
    <DashboardLayout>
      <DashboardNavbar absolute />
      <MDBox mt={10}>
        <MDBox py={2} px={2} mb={4}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="standard">
                <InputLabel>Entreprise</InputLabel>
                <Select
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value)
                    setCurrentReturnedPage(1)
                    setCurrentMissingPage(1)
                  }}
                >
                  <MenuItem value="all">Toutes les entreprises</MenuItem>
                  <MenuItem value="alamana">Alamana</MenuItem>
                  <MenuItem value="lazajella">Lazajella</MenuItem>
                  <MenuItem value="mylerz">Mylerz</MenuItem>
                  <MenuItem value="chahin">Chahin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </MDBox>

        <Grid container spacing={6}>
          {/* RETURNED SECTION */}
          <Grid item xs={12}>
            <MDBox mb={6} display="flex" justifyContent="flex-start">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Recherche instantanée..."
                  variant="outlined"
                  value={searchReturned}
                  onChange={handleReturnedSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </MDBox>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg">
                <MDTypography variant="h6" color="white">
                  Retournés ({allFilteredReturned.length})
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                {isReturnedDataLoading ? (
                  <MDBox display="flex" justifyContent="center" py={10}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <>
                    <DataTable
                      table={trackingPackagesDataTable(paginatedReturned)}
                      entriesPerPage={false}
                      showTotalEntries={false}
                    />
                    <MDBox p={3} display="flex" justifyContent="flex-end">
                      <Pagination
                        count={pageCount(allFilteredReturned.length)}
                        page={currentReturnedPage}
                        onChange={(e, v) => setCurrentReturnedPage(v)}
                        color="info"
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* MISSING SECTION */}
          <Grid item xs={12} mt={4}>
            <MDBox mb={6} display="flex" justifyContent="flex-start">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Recherche instantanée..."
                  variant="outlined"
                  value={searchMissing}
                  onChange={handleMissingSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </MDBox>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="warning" borderRadius="lg">
                <MDTypography variant="h6" color="white">
                  Manquants ({allFilteredMissing.length})
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                {isMissingDataLoading ? (
                  <MDBox display="flex" justifyContent="center" py={10}>
                    <CircularProgress color="warning" />
                  </MDBox>
                ) : (
                  <>
                    <DataTable
                      table={trackingPackagesDataTable(paginatedMissing)}
                      entriesPerPage={false}
                      showTotalEntries={false}
                    />
                    <MDBox p={3} display="flex" justifyContent="flex-end">
                      <Pagination
                        count={pageCount(allFilteredMissing.length)}
                        page={currentMissingPage}
                        onChange={(e, v) => setCurrentMissingPage(v)}
                        color="warning"
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <MDSnackbar {...snackbarObject} close={handleCloseSnackbar} />
    </DashboardLayout>
  )
}

export default Packages
